/**
 * Empty-until-configured media slots for /ai-for-ontario-teachers.
 *
 * Both variants render `null` (literally nothing — no empty frame, no
 * placeholder box) until the founder fills their entry in
 * `frontend/src/shared/ai_for_ontario_teachers_media.json`. A visitor
 * looking at the page with both slots empty cannot tell that anything
 * is missing.
 *
 * Populate mechanism (matches the JSON-driven pattern already used by
 * `the_path_content.json`):
 *
 *   VIDEO  — set `video` in the JSON to either
 *     { "src": "/media/atomic-edu-teacher-demo.mp4", "poster": "…", "caption": "…" }
 *   OR
 *     { "embedUrl": "https://www.youtube.com/embed/XXX", "caption": "…" }
 *
 *   RECEIPTS  — set `receipts` in the JSON to an array of 2-3 entries:
 *     [
 *       { "src": "/media/ai-receipt-1.png", "alt": "…", "caption": "…" },
 *       { "src": "/media/ai-receipt-2.png", "alt": "…", "caption": "…" },
 *       { "src": "/media/ai-receipt-3.png", "alt": "…", "caption": "…" }
 *     ]
 *
 * Image files go in `/app/frontend/public/media/`. No component
 * changes required to populate either slot.
 */

export function VideoSlot({ config, testId = "aiot-video-slot" }) {
  if (!config) return null;
  const { src, embedUrl, poster, caption } = config;
  if (!src && !embedUrl) return null;
  return (
    <figure
      data-testid={testId}
      className="my-12 mx-auto max-w-3xl"
    >
      <div className="aspect-video w-full overflow-hidden rounded-lg border border-[#4a6984]/15 bg-black shadow-sm">
        {embedUrl ? (
          <iframe
            src={embedUrl}
            title={caption || "Atomic Edu demo"}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            className="h-full w-full"
          />
        ) : (
          <video
            src={src}
            poster={poster}
            controls
            preload="metadata"
            className="h-full w-full object-cover"
          >
            <track kind="captions" />
          </video>
        )}
      </div>
      {caption ? (
        <figcaption className="mt-3 text-center text-sm text-[#4a6984]">
          {caption}
        </figcaption>
      ) : null}
    </figure>
  );
}

export function ReceiptsSlot({ items, testId = "aiot-receipts-slot" }) {
  const list = Array.isArray(items) ? items.filter((it) => it && it.src) : [];
  if (list.length === 0) return null;
  return (
    <div
      data-testid={testId}
      className="my-10 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3"
    >
      {list.slice(0, 3).map((item, idx) => (
        <figure key={`${item.src}-${idx}`} className="flex flex-col">
          <div className="overflow-hidden rounded-md border border-[#4a6984]/15 bg-white shadow-sm">
            <img
              src={item.src}
              alt={item.alt || ""}
              className="block w-full h-auto"
              loading="lazy"
            />
          </div>
          {item.caption ? (
            <figcaption className="mt-3 text-sm text-[#4a6984] leading-relaxed">
              {item.caption}
            </figcaption>
          ) : null}
        </figure>
      ))}
    </div>
  );
}
